import React, { useRef, useState } from 'react';

import { Telemetry } from '@videoblocks/kafka-rest-client';
import { UploadBottom, Close } from '@videoblocks/react-icons';

interface FileSearchModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const FileSearchModal: React.FC<FileSearchModalProps> = ({
  isOpen,
  onClose,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  if (!isOpen) return null;

  const handleChooseFile = () => {
    fileInputRef.current?.click();
  };

  const handleSubmitWithForm = (embedding: any) => {
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = '/audio/search';

    const hiddenInput = document.createElement('input');
    hiddenInput.type = 'hidden';
    hiddenInput.name = 'embedding';
    hiddenInput.value = JSON.stringify(embedding);

    form.appendChild(hiddenInput);

    document.body.appendChild(form);
    form.submit();
    document.body.removeChild(form);
  };

  const clearFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleClose = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setError(null);
    setIsLoading(false);
    clearFileInput();
    onClose();
  };

  const uploadFile = async (file: File) => {
    setIsLoading(true);
    abortControllerRef.current = new AbortController();

    const acceptedTypes = ['audio/mpeg', 'audio/wav'];

    if (!acceptedTypes.includes(file.type)) {
      setError('Error: file must be an .MP3 or .WAV.');
      setIsLoading(false);
      abortControllerRef.current = null;
      return;
    }

    try {
      void Telemetry.increment('search.bysound.click.upload');

      const formData = new FormData();
      formData.append('file', file);
      const response = await fetch('/api/audio/audio-embedding', {
        method: 'POST',
        body: formData,
        signal: abortControllerRef.current.signal,
      });
      const result = await response.json();
      const embedding = result?.data || [];
      if (!response.ok || embedding.length === 0) {
        throw new Error(`Upload failed: ${response.statusText}`);
      }
      clearFileInput();
      setError(null);
      void Telemetry.increment('search.bysound.upload_success');
      handleSubmitWithForm(embedding);
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        clearFileInput();
        return;
      }
      setError('Error: file must be an .MP3 or WAV under 7 minutes');
      clearFileInput();
    } finally {
      abortControllerRef.current = null;
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file && !isLoading) {
      void uploadFile(file);
    }
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);

    if (isLoading) return;

    const dt = event.dataTransfer;
    if (dt && dt.files && dt.files.length > 0) {
      const file = dt.files[0];
      void uploadFile(file);
    }
  };

  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
  };

  return (
    <div className="fixed inset-0 z-1000 flex items-center justify-center">
      {/* disable a11y checks / tab focus behavior for the modal backdrop */}
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions */}
      <div
        className="absolute inset-0 bg-black bg-opacity-50 transition-opacity"
        onClick={handleClose}
      />
      <div className="bg-white rounded-lg shadow-lg p-8 w-full max-w-2xl relative flex flex-col">
        <div className="border-b border-gray-400 pb-4 mb-6 justify-between flex items-center relative">
          <h2 className="text-base font-semibold text-gray-900 my-0">
            Search with Audio Clip
          </h2>
          <Close
            onClick={handleClose}
            aria-label="Close"
            className="w-4 h-4 fill-current text-gray-70 cursor-pointer"
          />
        </div>

        <div className="relative flex flex-col items-center justify-center">
          <div className={`absolute ${isLoading ? 'block' : 'hidden'}`}>
            <div className="flex items-center flex-col justify-center gap-3">
              <p className="text-gray-900 text-lg">Uploading your file...</p>
              <svg
                className="animate-spin"
                width="48"
                height="48"
                viewBox="0 0 48 48"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M4 24C4 35.0457 12.9543 44 24 44C35.0457 44 44 35.0457 44 24C44 12.9543 35.0457 4 24 4"
                  stroke="black"
                  stroke-width="4"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                />
              </svg>
            </div>
          </div>
          <div
            className={`bg-gray-100 border border-dashed rounded-lg flex flex-col items-center justify-center py-5 transition-colors w-full ${
              isDragging ? 'border-blue-400 bg-blue-50' : 'border-gray-500'
            } ${isLoading ? 'invisible' : 'visible'}`}
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
          >
            <UploadBottom className="w-6 h-6 mb-3 text-gray-400" />
            <p className="max-w-lg text-gray-900 text-lg text-center mb-2 font-semibold">
              Upload an audio clip to discover royalty-free music and sound
              effects with a similar sound.
            </p>
            <p className="text-gray-900 text-base text-center font-normal">
              Works with .MP3 or .WAV up to 7 minutes in length.
            </p>
            <button
              onClick={handleChooseFile}
              className="mt-3 bg-yellow-400 text-black font-semibold py-2 px-8 rounded-lg hover:bg-yellow-500 focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:ring-opacity-50 transition-colors border-none"
            >
              Choose File
            </button>

            {!!error?.length && (
              <p className="text-red-500 text-sm mt-4 mb-0">{error}</p>
            )}
          </div>
        </div>
      </div>
      <input
        type="file"
        accept="audio/mpeg,audio/wav"
        className="hidden"
        ref={fileInputRef}
        onChange={handleFileChange}
      />
    </div>
  );
};

export default FileSearchModal;
